import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Tooltip,
  Button,
  TextField,
  Avatar,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Pagination,
  Stack,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import CheckIcon from "@mui/icons-material/Check";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LockResetIcon from "@mui/icons-material/LockReset";
import TableSortLabel from "@mui/material/TableSortLabel";
import Drawer from "@mui/material/Drawer";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AddIcon from "@mui/icons-material/Add";
import * as XLSX from "xlsx";

import AddEmployeeModal from "./AddEmployeeModal";
import PersonelImportModal from "../components/PersoneImportModal";
import KurumImportModal from "../components/KurumImportModal";
import { useApi } from "../utils/api";
import { ENDPOINTS } from "../config/endpoints";

const Employee = () => {
  const navigate = useNavigate();
  const http = useApi();

  const [kurumlar, setKurumlar] = useState([]);
  const [selectedKurum, setSelectedKurum] = useState(null);
  const [izinler, setIzinler] = useState([]);
  const [izinLoading, setIzinLoading] = useState(false);
  const [personelImportOpen, setPersonelImportOpen] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // yeni izin formu
  const [newIzin, setNewIzin] = useState({
    izin_turu: "",
    baslangic_tarihi: "",
    bitis_tarihi: "",
    gun_sayisi: 0,
    ucretli_neden: "",
    aciklama: "",
  });

  // localStorage'dan kullanıcı verisini al
  const userData = JSON.parse(localStorage.getItem("user"));
  const isAdmin = userData?.type !== "kurum";
  const isSystemAdmin = userData?.type === "admin";

  // Filtreler
  const [departmanlar, setDepartmanlar] = useState([]);
  const [filterDepartmanId, setFilterDepartmanId] = useState("");
  const [filterCinsiyet, setFilterCinsiyet] = useState("");
  const [filterIstihdam, setFilterIstihdam] = useState("");

  // Şifre değiştirme
  const [sifreTarget, setSifreTarget] = useState(null);
  const [yeniSifre, setYeniSifre] = useState("");
  const [sifreLoading, setSifreLoading] = useState(false);

  // Sıralama
  const [sortBy, setSortBy] = useState(null); // 'ise_giris_tarihi' | 'istihdam_turu' | 'saatlik_ucret'
  const [sortDir, setSortDir] = useState("asc"); // 'asc' | 'desc'

  // Görünür dosya türleri
  const [visibleDocTypes, setVisibleDocTypes] = useState([]);

  const [search, setSearch] = useState("");
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [izinDrawerOpen, setIzinDrawerOpen] = useState(false);
  const [selectedEmployeeForIzin, setSelectedEmployeeForIzin] = useState(null);
  const [kurumImportOpen, setKurumImportOpen] = useState(false);
  const [kurumlarLoading, setKurumlarLoading] = useState(false);
  const [aktifTab, setAktifTab] = useState("aktif");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [cikarTarget, setCikarTarget] = useState(null);
  const [cikarTarihi, setCikarTarihi] = useState("");
  const [cikarLoading, setCikarLoading] = useState(false);

  const openIzinDrawer = (emp) => {
    setSelectedEmployeeForIzin(emp);
    setIzinDrawerOpen(true);
  };

  const closeIzinDrawer = () => {
    setIzinDrawerOpen(false);
    setSelectedEmployeeForIzin(null);
  };

  const fetchEmployees = async () => {
    setLoading(true);
    setError("");

    try {
      // Tüm kayıtları çek; sayfalama/arama/tab filtresi client-side yapılıyor
      const params = {
        skip: 0,
        limit: 10000,
      };

      if (isAdmin && selectedKurum) {
        params.kurum_id = Number(selectedKurum);
      }

      if (!isAdmin && userData?.id) {
        params.kurum_id = userData.id;
      }

      if (filterDepartmanId) params.departman_id = Number(filterDepartmanId);
      if (filterCinsiyet) params.cinsiyet = filterCinsiyet;
      if (filterIstihdam) params.istihdam_turu = filterIstihdam;

      const data = await http.get(ENDPOINTS.PERSONEL.LIST, params);

      setEmployees(Array.isArray(data) ? data : []);

    } catch (err) {
      setError(err.message || "Veri çekilirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin && kurumlarLoading) return;
    if (isAdmin && !selectedKurum) return;

    fetchEmployees();
  }, [http, selectedKurum, isAdmin, kurumlarLoading, filterDepartmanId, filterCinsiyet, filterIstihdam]);

  // Departmanlar (filtre için)
  useEffect(() => {
    http.get("/departmanlar", { skip: 0, limit: 200 })
      .then((d) => setDepartmanlar(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [http]);

  // Görünür dosya türleri (tablo dots kolonu için)
  useEffect(() => {
    http.get(ENDPOINTS.DOSYA_TURU.LIST, { only_aktif: true, only_gorunur: true })
      .then((d) => setVisibleDocTypes(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [http]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  const sortedEmployees = React.useMemo(() => {
    const arr = [...employees];
    if (sortBy) {
      const dir = sortDir === "asc" ? 1 : -1;
      arr.sort((a, b) => {
        if (sortBy === "ad") {
          const cmp = (a?.ad || "").localeCompare(b?.ad || "", "tr", { sensitivity: "base" });
          if (cmp !== 0) return cmp * dir;
          return (a?.soyad || "").localeCompare(b?.soyad || "", "tr", { sensitivity: "base" }) * dir;
        }
        let va = a?.[sortBy];
        let vb = b?.[sortBy];
        if (sortBy === "saatlik_ucret") {
          va = Number(va || 0);
          vb = Number(vb || 0);
        }
        if (va == null) va = "";
        if (vb == null) vb = "";
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
    }
    return arr;
  }, [employees, sortBy, sortDir]);

  useEffect(() => {
    if (!isAdmin) return;

    const fetchKurumlar = async () => {
      setKurumlarLoading(true);
      try {
        const data = await http.get("/kurumlar");
        setKurumlar(Array.isArray(data) ? data : []);

        if (data?.length > 0) {
          setSelectedKurum(data[0].id);
        }
      } catch (err) {
        console.error("Kurumlar yüklenemedi", err);
      } finally {
        setKurumlarLoading(false);
      }
    };

    fetchKurumlar();
  }, [http, isAdmin]);

  const handleAddIzin = async () => {
    if (!newIzin.izin_turu || !newIzin.baslangic_tarihi || !newIzin.bitis_tarihi)
      return;

    const payload = {
      ...newIzin,
      personel_id: selectedEmployeeForIzin.id,
    };

    const created = await http.post(ENDPOINTS.IZIN.CREATE, payload);

    setIzinler((prev) => [...prev, created]);

    setNewIzin({
      izin_turu: "",
      baslangic_tarihi: "",
      bitis_tarihi: "",
      gun_sayisi: 0,
      ucretli_neden: "",
      aciklama: "",
    });
  };

  useEffect(() => {
    if (!selectedEmployeeForIzin?.id) return;

    setIzinLoading(true);

    http
      .get(ENDPOINTS.IZIN.LIST_BY_PERSONEL(selectedEmployeeForIzin.id))
      .then((data) => {
        setIzinler(Array.isArray(data) ? data : []);
      })
      .finally(() => setIzinLoading(false));
  }, [selectedEmployeeForIzin, http]);

  const filteredEmployees = sortedEmployees.filter((emp) => {
    const fullName = `${emp.ad || ""} ${emp.soyad || ""}`.toLowerCase();
    const nameMatch = fullName.includes(search.toLowerCase());
    const aktifMatch = aktifTab === "aktif" ? emp.aktif !== false : emp.aktif === false;
    return nameMatch && aktifMatch;
  });

  // Client-side sayfalama
  const totalCount = filteredEmployees.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

  // Filtre/arama sonrası mevcut sayfa aralık dışına düşerse başa sar
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const pagedEmployees = filteredEmployees.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const formatSalary = (value) => {
    if (!value && value !== 0) return "-";
    const num = Number(value);
    return isNaN(num)
      ? value
      : `${num.toLocaleString("tr-TR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ₺`;
  };

  const formatIstihdamTuru = (value) =>
    value
      ? value
          .split("_")
          .map((w) => w[0].toUpperCase() + w.slice(1))
          .join(" ")
      : "-";

  const formatUcretTuru = (value) => {
    if (value === "net") return "Net Ücret";
    if (value === "brut") return "Brüt Ücret";
    return "-";
  };

  const openAddModal = () => {
    setEditingEmployee(null);
    setAddModalOpen(true);
  };

  const openEditModal = (emp) => {
    setEditingEmployee(emp);
    setAddModalOpen(true);
  };

  const handleEmployeeAdded = (newEmp) => {
    setEmployees((prev) => [...prev, newEmp]);
  };

  const handleEmployeeUpdated = (updatedEmp) => {
    setEmployees((prev) =>
      prev.map((emp) => (emp.id === updatedEmp.id ? updatedEmp : emp))
    );
  };

  const handleDeleteClick = (emp) => {
    setDeleteTarget(emp);
  };

  const handleConfirmDelete = async () => {
    const emp = deleteTarget;
    if (!emp) return;

    setDeleteTarget(null);
    setDeletingId(emp.id);

    const originalList = [...employees];

    setTimeout(async () => {
      setEmployees((prev) => prev.filter((e) => e.id !== emp.id));

      try {
        await http.del(ENDPOINTS.PERSONEL.DETAIL(emp.id));
      } catch (err) {
        setError(err.message || "Silme başarısız.");
        setEmployees(originalList);
      } finally {
        setDeletingId(null);
      }
    }, 200);
  };

  const handleCancelDelete = () => {
    setDeleteTarget(null);
  };

  const handleCikarClick = (emp) => {
    setCikarTarihi(new Date().toISOString().slice(0, 10));
    setCikarTarget(emp);
  };

  const handleConfirmCikar = async () => {
    const emp = cikarTarget;
    if (!emp) return;

    setCikarLoading(true);
    try {
      const params = cikarTarihi ? `?tarih=${cikarTarihi}` : "";
      const updated = await http.put(ENDPOINTS.PERSONEL.ISTEN_CIKAR(emp.id) + params);
      setEmployees((prev) =>
        prev.map((e) => (e.id === updated.id ? updated : e))
      );
      setCikarTarget(null);
    } catch (err) {
      setError(err.message || "İşten çıkarma başarısız.");
    } finally {
      setCikarLoading(false);
    }
  };

  const handleCancelCikar = () => {
    setCikarTarget(null);
  };

  const openSifreDialog = (emp) => {
    setYeniSifre("");
    setSifreTarget(emp);
  };

  const handleSifreSave = async () => {
    if (!sifreTarget || !yeniSifre) return;
    setSifreLoading(true);
    try {
      await http.put(`/personeller/${sifreTarget.id}/sifre`, { sifre: yeniSifre });
      setSifreTarget(null);
      setYeniSifre("");
    } catch (err) {
      setError(err.message || "Şifre güncellenemedi.");
    } finally {
      setSifreLoading(false);
    }
  };

  const handleExportExcel = () => {
    if (!filteredEmployees.length) return;

    const exportData = filteredEmployees.map((emp) => ({
      İsim: `${emp.ad || ""} ${emp.soyad || ""}`.trim(),
      Telefon: emp.telefon || "",
      Mail: emp.mail || "",
      "İstihdam Türü": formatIstihdamTuru(emp.istihdam_turu),
      "İşe Giriş": emp.ise_giris_tarihi || "",
      "Ücret Türü": formatUcretTuru(emp.ucret_turu),
      "Saatlik Ücret": emp.saatlik_ucret ?? "",
      "Çocuk Sayısı": emp.cocuk_sayisi ?? "",
      "Evli mi": emp.evli_mi ? "Evet" : "Hayır",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Personeller");

    XLSX.writeFile(wb, "personeller.xlsx");
  };

  return (
    <Box>
      {/* Üst Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Personel Sayfası
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Tüm personeli yönetin.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title="Filtre">
            <IconButton>
              <FilterListIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{ ml: 1 }}
            onClick={handleExportExcel}
            disabled={filteredEmployees.length === 0 || loading}
          >
            Excele Aktar
          </Button>

          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            sx={{ ml: 1 }}
            onClick={openAddModal}
          >
            Personel Ekle
          </Button>
        </Box>
      </Box>

      <TextField
        placeholder="İsim ara..."
        variant="outlined"
        size="small"
        fullWidth
        sx={{ mb: 2 }}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
      />

      {/* Aktif / Pasif Tab */}
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        {["aktif", "pasif"].map((tab) => (
          <Box
            key={tab}
            onClick={() => {
              setAktifTab(tab);
              setPage(1);
            }}
            sx={{
              px: 3,
              py: 0.8,
              borderRadius: 2,
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
              bgcolor:
                aktifTab === tab
                  ? tab === "aktif"
                    ? "#DCFCE7"
                    : "#F3F4F6"
                  : "transparent",
              color:
                aktifTab === tab
                  ? tab === "aktif"
                    ? "#16A34A"
                    : "#6B7280"
                  : "text.secondary",
              border: "1px solid",
              borderColor:
                aktifTab === tab
                  ? tab === "aktif"
                    ? "#86EFAC"
                    : "#D1D5DB"
                  : "transparent",
              transition: "all 0.2s",
              "&:hover": {
                bgcolor: tab === "aktif" ? "#DCFCE7" : "#F3F4F6",
              },
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {tab === "aktif" ? "✓ Aktif Personel" : "○ Pasif Personel"}
            <Box
              sx={{
                bgcolor:
                  aktifTab === tab
                    ? tab === "aktif"
                      ? "#16A34A"
                      : "#9CA3AF"
                    : "#E5E7EB",
                color: "white",
                borderRadius: 999,
                px: 1,
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {
                employees.filter((e) =>
                  tab === "aktif" ? e.aktif !== false : e.aktif === false
                ).length
              }
            </Box>
          </Box>
        ))}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isAdmin && (
        <FormControl size="small" sx={{ mb: 2, minWidth: 250 }}>
          <InputLabel>Kurum Seç</InputLabel>
          <Select
            value={Number(selectedKurum)}
            label="Kurum Seç"
            onChange={(e) => {
              setSelectedKurum(Number(e.target.value));
              setPage(1);
            }}
          >
            {kurumlar?.map((k) => (
              <MenuItem key={k.id} value={k.id}>
                {k?.kurum_adi}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Filtreler */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Departman</InputLabel>
          <Select
            value={filterDepartmanId}
            label="Departman"
            onChange={(e) => { setFilterDepartmanId(e.target.value); setPage(1); }}
          >
            <MenuItem value=""><em>Hepsi</em></MenuItem>
            {departmanlar.map((d) => (
              <MenuItem key={d.id} value={d.id}>{d.ad}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Cinsiyet</InputLabel>
          <Select
            value={filterCinsiyet}
            label="Cinsiyet"
            onChange={(e) => { setFilterCinsiyet(e.target.value); setPage(1); }}
          >
            <MenuItem value=""><em>Hepsi</em></MenuItem>
            <MenuItem value="erkek">Erkek</MenuItem>
            <MenuItem value="kadin">Kadın</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>İstihdam Türü</InputLabel>
          <Select
            value={filterIstihdam}
            label="İstihdam Türü"
            onChange={(e) => { setFilterIstihdam(e.target.value); setPage(1); }}
          >
            <MenuItem value=""><em>Hepsi</em></MenuItem>
            <MenuItem value="kadrolu_ogretmen">Kadrolu Öğretmen</MenuItem>
            <MenuItem value="ders_saati_ucretli_ogretmen">Ders Saati Ücretli Öğretmen</MenuItem>
            <MenuItem value="tam_zamanli_yardimci_personel">Tam Zamanlı Personel</MenuItem>
            <MenuItem value="kismi_zamanli_yardimci_personel">Kısmi Zamanlı Personel</MenuItem>
          </Select>
        </FormControl>

        {(filterDepartmanId || filterCinsiyet || filterIstihdam) && (
          <Button
            size="small"
            onClick={() => {
              setFilterDepartmanId("");
              setFilterCinsiyet("");
              setFilterIstihdam("");
              setPage(1);
            }}
          >
            Filtreleri Temizle
          </Button>
        )}
      </Stack>

      <Paper sx={{ width: "100%", overflowX: "auto" }}>
        <Table>
          <TableHead sx={{ bgcolor: "#f5f5f5" }}>
            <TableRow>
              <TableCell align="center" sx={{ color: "gray", fontWeight: 600 }}>
                <TableSortLabel
                  active={sortBy === "ad"}
                  direction={sortBy === "ad" ? sortDir : "asc"}
                  onClick={() => handleSort("ad")}
                >
                  İsim
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ color: "gray", fontWeight: 600 }}>TC</TableCell>
              <TableCell align="center" sx={{ color: "gray", fontWeight: 600 }}>
                <TableSortLabel
                  active={sortBy === "istihdam_turu"}
                  direction={sortBy === "istihdam_turu" ? sortDir : "asc"}
                  onClick={() => handleSort("istihdam_turu")}
                >
                  İstihdam Türü
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ color: "gray", fontWeight: 600 }}>
                <TableSortLabel
                  active={sortBy === "saatlik_ucret"}
                  direction={sortBy === "saatlik_ucret" ? sortDir : "asc"}
                  onClick={() => handleSort("saatlik_ucret")}
                >
                  Saatlik Ücret
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ color: "gray", fontWeight: 600 }}>
                <TableSortLabel
                  active={sortBy === "ise_giris_tarihi"}
                  direction={sortBy === "ise_giris_tarihi" ? sortDir : "asc"}
                  onClick={() => handleSort("ise_giris_tarihi")}
                >
                  İşe Giriş
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ color: "gray", fontWeight: 600 }}>Eksik Dosya</TableCell>
              {visibleDocTypes.length > 0 && (
                <TableCell align="center" sx={{ color: "gray", fontWeight: 600 }}>Dosyalar</TableCell>
              )}
              <TableCell align="center" sx={{ color: "gray", fontWeight: 600 }}>Ücretli İzin</TableCell>
              <TableCell align="center" sx={{ color: "gray", fontWeight: 600 }}>Rapor</TableCell>
              <TableCell align="center" sx={{ color: "gray", fontWeight: 600 }}>Kullanılan İzin</TableCell>
              <TableCell align="center" sx={{ color: "gray", fontWeight: 600 }}>Kalan İzin</TableCell>
              <TableCell align="center" sx={{ color: "gray", fontWeight: 600 }}>İşlemler</TableCell>
              <TableCell align="center" sx={{ color: "gray", fontWeight: 600 }}>İzinler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={20} /> Yükleniyor...
                </TableCell>
              </TableRow>
            )}
            {!loading && filteredEmployees.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" color="text.secondary">
                  Kayıt bulunamadı.
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              pagedEmployees.map((emp) => (
                <TableRow
                  key={emp.id}
                  hover
                  sx={{
                    transition: "opacity 0.2s ease, transform 0.2s ease",
                    opacity: deletingId === emp.id ? 0 : 1,
                    transform:
                      deletingId === emp.id ? "translateX(16px)" : "none",
                    bgcolor: emp.aktif === false ? "#F3F4F6" : "inherit",
                  }}
                >
                  <TableCell align="center">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          bgcolor: "#DBEAFE",
                          fontSize: 12,
                          color: "#2563EB",
                        }}
                      >
                        {(emp.ad || "P")[0].toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {emp.ad} {emp.soyad}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">{emp.tc || "-"}</TableCell>
                  <TableCell align="center">
                    {formatIstihdamTuru(emp.istihdam_turu)}
                  </TableCell>
                  <TableCell align="center">
                    {formatSalary(emp.saatlik_ucret)}
                  </TableCell>
                  <TableCell align="center">
                    {emp.ise_giris_tarihi || "-"}
                  </TableCell>

                  <TableCell align="center">
                    {emp.eksik_dosya_sayisi !== undefined ? (
                      emp.eksik_dosya_sayisi === 0 ? (
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            backgroundColor: "#10B981",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mx: "auto",
                          }}
                        >
                          <CheckIcon sx={{ color: "white", fontSize: 22 }} />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mx: "auto",
                            fontWeight: 700,
                            color: "#fff",
                            backgroundColor: () => {
                              const val = emp.eksik_dosya_sayisi;
                              if (val >= 8) return "#EF4444";
                              if (val >= 4) return "#F59E0B";
                              if (val >= 1) return "#6A8CFC";
                              return "#6B7280";
                            },
                          }}
                        >
                          {emp.eksik_dosya_sayisi}
                        </Box>
                      )
                    ) : (
                      "-"
                    )}
                  </TableCell>

                  {visibleDocTypes.length > 0 && (
                    <TableCell align="center">
                      <Box sx={{ display: "inline-flex", gap: 0.5, flexWrap: "wrap", maxWidth: 220, justifyContent: "center" }}>
                        {visibleDocTypes.map((dt) => {
                          const has = (emp.dosya_turleri_kodlari || []).includes(dt.kod);
                          return (
                            <Tooltip key={dt.kod} title={`${dt.ad} — ${has ? "var" : "yok"}`}>
                              <Box sx={{
                                width: 14,
                                height: 14,
                                borderRadius: "50%",
                                bgcolor: has ? "#10B981" : "#E5E7EB",
                                border: has ? "none" : "1px solid #D1D5DB",
                              }} />
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </TableCell>
                  )}

                  <TableCell align="center">
                    <Box sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 28,
                      height: 28,
                      px: 1,
                      borderRadius: 999,
                      bgcolor: (emp.ucretli_izin_sayisi || 0) > 0 ? "#FEF3C7" : "#F3F4F6",
                      color: (emp.ucretli_izin_sayisi || 0) > 0 ? "#92400E" : "#6B7280",
                      fontWeight: 700,
                      fontSize: 12,
                    }}>
                      {emp.ucretli_izin_sayisi ?? 0}
                    </Box>
                  </TableCell>

                  <TableCell align="center">
                    <Box sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 28,
                      height: 28,
                      px: 1,
                      borderRadius: 999,
                      bgcolor: (emp.rapor_sayisi || 0) > 0 ? "#FECACA" : "#F3F4F6",
                      color: (emp.rapor_sayisi || 0) > 0 ? "#991B1B" : "#6B7280",
                      fontWeight: 700,
                      fontSize: 12,
                    }}>
                      {emp.rapor_sayisi ?? 0}
                    </Box>
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title={`Hak edilen: ${emp.yillik_izin_hakki ?? 0} gün`}>
                      <Box sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 32,
                        height: 28,
                        px: 1,
                        borderRadius: 999,
                        bgcolor: (emp.kullanilan_yillik_izin || 0) > 0 ? "#E0E7FF" : "#F3F4F6",
                        color: (emp.kullanilan_yillik_izin || 0) > 0 ? "#3730A3" : "#6B7280",
                        fontWeight: 700,
                        fontSize: 12,
                      }}>
                        {emp.kullanilan_yillik_izin ?? 0} gün
                      </Box>
                    </Tooltip>
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title={`Hak: ${emp.yillik_izin_hakki ?? 0} − Kullanılan: ${emp.kullanilan_yillik_izin ?? 0}`}>
                      <Box sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 32,
                        height: 28,
                        px: 1,
                        borderRadius: 999,
                        bgcolor:
                          (emp.kalan_yillik_izin || 0) === 0
                            ? (emp.yillik_izin_hakki || 0) === 0 ? "#F3F4F6" : "#FECACA"
                            : "#DCFCE7",
                        color:
                          (emp.kalan_yillik_izin || 0) === 0
                            ? (emp.yillik_izin_hakki || 0) === 0 ? "#6B7280" : "#991B1B"
                            : "#166534",
                        fontWeight: 700,
                        fontSize: 12,
                      }}>
                        {emp.kalan_yillik_izin ?? 0} gün
                      </Box>
                    </Tooltip>
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title="Görüntüle">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/personel/${emp.id}`)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Düzenle">
                      <IconButton
                        size="small"
                        onClick={() => openEditModal(emp)}
                      >
                        <EditNoteOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {emp.aktif !== false && (
                      <Tooltip title="İşten Çıkar">
                        <IconButton
                          size="small"
                          sx={{ color: "#D97706" }}
                          onClick={() => handleCikarClick(emp)}
                        >
                          <ExitToAppIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isSystemAdmin && (
                      <Tooltip title="Şifre Değiştir">
                        <IconButton
                          size="small"
                          sx={{ color: "#7C3AED" }}
                          onClick={() => openSifreDialog(emp)}
                        >
                          <LockResetIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isSystemAdmin && (
                      <Tooltip title="Sil">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(emp)}
                        >
                          <PersonOffOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="İzinler">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openIzinDrawer(emp)}
                      >
                        <EventAvailableIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Pagination */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Sayfa başı:
          </Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </Box>

        <Pagination
          count={pageCount}
          page={page}
          onChange={(_, val) => setPage(val)}
          color="primary"
          shape="rounded"
          showFirstButton
          showLastButton
        />

        <Typography variant="body2" color="text.secondary">
          Toplam: {totalCount} kayıt
        </Typography>
      </Stack>

      <AddEmployeeModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        employee={editingEmployee}
        onEmployeeAdded={handleEmployeeAdded}
        onEmployeeUpdated={handleEmployeeUpdated}
        fetchEmployees={fetchEmployees}
        userData={userData}
      />

      {/* Silme Onayı Diyaloğu */}
      <Dialog
        open={!!deleteTarget}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Personel Silinsin mi?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            <strong>
              {deleteTarget?.ad} {deleteTarget?.soyad}
            </strong>{" "}
            kaydını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Vazgeç</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
          >
            Evet, Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Şifre Değiştirme */}
      <Dialog
        open={!!sifreTarget}
        onClose={() => !sifreLoading && setSifreTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Şifre Değiştir</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{sifreTarget?.ad} {sifreTarget?.soyad}</strong> için yeni şifre belirle.
          </Typography>
          <TextField
            type="password"
            label="Yeni Şifre"
            value={yeniSifre}
            onChange={(e) => setYeniSifre(e.target.value)}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSifreTarget(null)} disabled={sifreLoading}>Vazgeç</Button>
          <Button
            variant="contained"
            onClick={handleSifreSave}
            disabled={sifreLoading || !yeniSifre}
          >
            {sifreLoading ? <CircularProgress size={18} /> : "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* İşten Çıkarma Onayı */}
      <Dialog
        open={!!cikarTarget}
        onClose={handleCancelCikar}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>İşten Çıkarma</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>
              {cikarTarget?.ad} {cikarTarget?.soyad}
            </strong>{" "}
            personelini işten çıkarmak istediğinizden emin misiniz? Personel
            pasif duruma alınacak.
          </Typography>
          <TextField
            type="date"
            label="Çıkış Tarihi"
            value={cikarTarihi}
            onChange={(e) => setCikarTarihi(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCikar} disabled={cikarLoading}>
            Vazgeç
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmCikar}
            disabled={cikarLoading || !cikarTarihi}
          >
            {cikarLoading ? <CircularProgress size={18} /> : "İşten Çıkar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={izinDrawerOpen}
        onClose={closeIzinDrawer}
        PaperProps={{ sx: { width: 420, p: 2 } }}
      >
        <Typography variant="h6" fontWeight={600}>
          Personel İzinleri
        </Typography>

        <Typography variant="body2" color="text.secondary" mb={2}>
          {selectedEmployeeForIzin?.ad} {selectedEmployeeForIzin?.soyad}
        </Typography>

        {izinLoading ? (
          <CircularProgress size={20} />
        ) : izinler.length === 0 ? (
          <Alert severity="info">Henüz izin girilmemiş</Alert>
        ) : (
          izinler.map((izin) => (
            <Paper key={izin.id} sx={{ p: 1.5, mb: 1 }}>
              <Typography fontWeight={600}>
                {izin.izin_turu.toUpperCase()}
              </Typography>
              <Typography variant="body2">
                {izin.baslangic_tarihi} → {izin.bitis_tarihi} ({izin.gun_sayisi} gün)
              </Typography>
              {izin.ucretli_neden && (
                <Typography variant="caption">
                  Neden: {izin.ucretli_neden}
                </Typography>
              )}
            </Paper>
          ))
        )}

        <Divider sx={{ my: 2 }} />

        <Typography fontWeight={600} mb={1}>
          Yeni İzin Ekle
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
          <InputLabel>İzin Türü</InputLabel>
          <Select
            value={newIzin.izin_turu}
            label="İzin Türü"
            onChange={(e) =>
              setNewIzin({ ...newIzin, izin_turu: e.target.value })
            }
          >
            <MenuItem value="yillik">Yıllık İzin</MenuItem>
            <MenuItem value="ucretsiz">Ücretsiz İzin</MenuItem>
            <MenuItem value="rapor">Rapor</MenuItem>
            <MenuItem value="ucretli">Ücretli İzin</MenuItem>
          </Select>
        </FormControl>

        <TextField
          type="date"
          size="small"
          fullWidth
          sx={{ mb: 1 }}
          label="Başlangıç"
          InputLabelProps={{ shrink: true }}
          value={newIzin.baslangic_tarihi}
          onChange={(e) =>
            setNewIzin({ ...newIzin, baslangic_tarihi: e.target.value })
          }
        />

        <TextField
          type="date"
          size="small"
          fullWidth
          sx={{ mb: 1 }}
          label="Bitiş"
          InputLabelProps={{ shrink: true }}
          value={newIzin.bitis_tarihi}
          onChange={(e) =>
            setNewIzin({ ...newIzin, bitis_tarihi: e.target.value })
          }
        />

        {newIzin.izin_turu === "ucretli" && (
          <TextField
            size="small"
            fullWidth
            sx={{ mb: 1 }}
            label="Ücretli İzin Nedeni"
            placeholder="Babalık, Ölüm, Evlilik..."
            value={newIzin.ucretli_neden}
            onChange={(e) =>
              setNewIzin({ ...newIzin, ucretli_neden: e.target.value })
            }
          />
        )}

        <TextField
          size="small"
          fullWidth
          label="Açıklama"
          multiline
          rows={2}
          value={newIzin.aciklama}
          onChange={(e) =>
            setNewIzin({ ...newIzin, aciklama: e.target.value })
          }
        />

        <Button
          startIcon={<AddIcon />}
          variant="contained"
          fullWidth
          sx={{ mt: 2 }}
          onClick={handleAddIzin}
        >
          İzin Ekle
        </Button>
      </Drawer>

      <KurumImportModal
        open={kurumImportOpen}
        onClose={() => setKurumImportOpen(false)}
        onSuccess={() => {
          if (isAdmin) {
            http.get("/kurumlar").then(setKurumlar);
          }
        }}
      />

      <PersonelImportModal
        open={personelImportOpen}
        onClose={() => setPersonelImportOpen(false)}
        onSuccess={() => {
          fetchEmployees();
        }}
      />
    </Box>
  );
};

export default Employee;
